// useModal — the shared overlay behaviour every modal adopts (#139).
//
// One hook that owns the things each overlay (Lightbox / Contact sheet / Mosaic)
// previously hand-rolled or skipped: move focus INTO the panel on open, TRAP Tab
// within it, close on Escape or a backdrop click, and RESTORE focus to wherever it
// was on close. Every close attempt routes through `requestClose`, which consults a
// `canClose` predicate first — so a Mosaic mid-encode can refuse to close (its busy
// guard) without each call site re-implementing the check.
//
// See cloud/CONTEXT.md → "modal / overlay" (and the firmware-"mode" disambiguation).

import { useCallback, useEffect, useRef } from "react";

export interface UseModalOptions {
  /** Close the overlay — invoked for Escape, a backdrop click, and the × (all via requestClose). */
  onClose: () => void;
  /**
   * Gate every close attempt: return false to refuse it (e.g. a Mosaic mid-encode).
   * Read FRESH on each attempt, so the predicate may close over live state. Defaults
   * to always-closeable.
   */
  canClose?: () => boolean;
}

export interface UseModalResult<T extends HTMLElement = HTMLDivElement> {
  /** Attach to the overlay PANEL element — the focus-trap root. */
  panelRef: React.RefObject<T | null>;
  /** A close that respects `canClose`. Wire the × and any programmatic close through this. */
  requestClose: () => void;
  /** Backdrop click handler — closes only when the click lands on the backdrop itself. */
  onBackdropClick: (e: React.MouseEvent) => void;
}

const FOCUSABLE =
  'a[href],area[href],button:not([disabled]),input:not([disabled]),' +
  'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Body scroll lock, ref-counted for stacked overlays (lightbox → contact sheet): the first
// open stores + replaces body overflow, only the last close restores it. Without this a
// wheel/touch over the dimmed backdrop scrolls the page behind the fixed overlay (and
// scroll-chains on mobile).
let scrollLocks = 0;
let prevBodyOverflow = "";
function lockBodyScroll(): void {
  if (scrollLocks++ === 0) {
    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
}
function unlockBodyScroll(): void {
  if (scrollLocks > 0 && --scrollLocks === 0) {
    document.body.style.overflow = prevBodyOverflow;
  }
}

export function useModal<T extends HTMLElement = HTMLDivElement>(
  options: UseModalOptions,
): UseModalResult<T> {
  const panelRef = useRef<T | null>(null);
  // Latest callbacks in refs so the keydown listener binds ONCE and never re-registers
  // when the caller passes fresh closures each render (canClose tracks live state).
  const onCloseRef = useRef(options.onClose);
  const canCloseRef = useRef(options.canClose);
  onCloseRef.current = options.onClose;
  canCloseRef.current = options.canClose;

  const requestClose = useCallback(() => {
    const gate = canCloseRef.current;
    if (!gate || gate()) onCloseRef.current();
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    lockBodyScroll();
    // Remember where focus was so we can restore it on close (don't strand keyboard users).
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move focus into the overlay: first focusable, else the panel itself (made focusable).
    const initial = panel?.querySelector<HTMLElement>(FOCUSABLE);
    if (initial) {
      initial.focus();
    } else if (panel) {
      panel.tabIndex = -1;
      panel.focus();
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.stopPropagation();
        requestClose();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      // Focus trap: keep Tab cycling within the panel's focusables.
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (active && !panel.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", onKeyDown, true);
      // Restore focus only if the opener is still in the DOM — if it was unmounted while the
      // overlay was open, leave focus on <body> rather than calling focus() on a detached node.
      if (previouslyFocused?.isConnected && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [requestClose]);

  const onBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) requestClose();
    },
    [requestClose],
  );

  return { panelRef, requestClose, onBackdropClick };
}
